import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const FAQSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFAQs, setFilteredFAQs] = useState([]);

  const translations = {
    en: {
      title: "Frequently Asked Questions",
      subtitle: "Get answers to common questions about our aluminum extrusion ERP system",
      searchPlaceholder: "Search FAQs...",
      faqs: [
        {
          category: "Implementation",
          question: "How long does it take to implement the ERP system?",
          answer: `Our standard implementation takes 12 weeks on average, following our proven 6-phase methodology:\n\n• Assessment & Planning (2 weeks)\n• System Setup & Configuration (2 weeks)\n• Data Migration (2 weeks)\n• Testing & Quality Assurance (2 weeks)\n• Training & Knowledge Transfer (2 weeks)\n• Go-Live & Support (2 weeks)\n\nThe timeline may vary based on your specific requirements and complexity of customizations needed.`,
          videoUrl: "https://example.com/implementation-video"
        },
        {
          category: "Cost & Pricing",
          question: "What are the total costs involved including implementation?",
          answer: `Our pricing is transparent with no hidden costs:\n\n• Software License: Starting from ₹15,000/month\n• Implementation: One-time fee of ₹5,00,000 (included in Enterprise plan)\n• Training: Included in all plans\n• Support: 24/7 support included\n• Customizations: Quoted separately based on requirements\n\nAll prices include 18% GST. We also offer flexible payment terms and financing options.`
        },
        {
          category: "Features",
          question: "Does the system handle aluminum-specific requirements like alloy tracking?",
          answer: `Yes, our ERP is specifically designed for aluminum extrusion manufacturers:\n\n• Alloy composition tracking and management\n• Die management and maintenance scheduling\n• Extrusion process parameters monitoring\n• Heat treatment tracking\n• Surface treatment management\n• Aluminum-specific quality control protocols\n• Scrap and recycling management\n• Industry-specific reporting and analytics`
        },
        {
          category: "Integration",
          question: "Can it integrate with our existing accounting software and machines?",
          answer: `Yes, we support extensive integrations:\n\n• Accounting Software: Tally, SAP, QuickBooks, Zoho Books\n• Manufacturing Equipment: PLC integration, IoT sensors\n• Quality Equipment: CMM machines, testing equipment\n• Logistics: Transport management systems\n• Banking: Payment gateways, bank reconciliation\n• E-commerce: B2B portals, marketplace integration\n\nOur API-first architecture ensures seamless connectivity with most third-party systems.`
        },
        {
          category: "Data Security",
          question: "How secure is our data and what about compliance?",
          answer: `We maintain enterprise-grade security:\n\n• ISO 27001 certified data centers\n• 256-bit SSL encryption for data transmission\n• AES-256 encryption for data at rest\n• Regular security audits and penetration testing\n• GDPR and Indian data protection compliance\n• Role-based access control\n• Automated daily backups with 99.9% uptime SLA\n• Disaster recovery with 4-hour RTO`
        },
        {
          category: "Support",
          question: "What kind of support do you provide after implementation?",
          answer: `We provide comprehensive ongoing support:\n\n• 24/7 technical support via phone, email, and chat\n• Dedicated account manager for Enterprise clients\n• Regular system health checks and optimization\n• Free software updates and security patches\n• Online training resources and documentation\n• User community forum\n• On-site support available (chargeable)\n• Annual system review and optimization recommendations`
        },
        {
          category: "Customization",
          question: "Can the system be customized for our specific processes?",
          answer: `Absolutely! Our ERP is highly customizable:\n\n• Custom workflows and approval processes\n• Industry-specific forms and reports\n• Custom dashboards and KPI tracking\n• Specialized modules for unique requirements\n• Custom integrations with proprietary systems\n• Branded user interface with your company colors\n• Custom mobile apps for specific use cases\n\nOur team works closely with you to understand and implement your specific requirements.`
        },
        {
          category: "Training",
          question: "How do you ensure our team can effectively use the system?",
          answer: `We provide comprehensive training programs:\n\n• Role-based training for different user types\n• Hands-on training with your actual data\n• Train-the-trainer programs for internal champions\n• Video tutorials and documentation\n• Regular refresher training sessions\n• New employee onboarding support\n• Advanced feature training as you grow\n• Certification programs for power users`
        }
      ],
      noResults: "No FAQs found matching your search.",
      watchVideo: "Watch Video Explanation"
    },
    hi: {
      title: "अक्सर पूछे जाने वाले प्रश्न",
      subtitle: "हमारे एल्यूमीनियम एक्सट्रूज़न ERP सिस्टम के बारे में सामान्य प्रश्नों के उत्तर प्राप्त करें",
      searchPlaceholder: "FAQ खोजें...",
      faqs: [
        {
          category: "कार्यान्वयन",
          question: "ERP सिस्टम को लागू करने में कितना समय लगता है?",
          answer: `हमारा मानक कार्यान्वयन औसतन 12 सप्ताह लेता है, हमारी सिद्ध 6-चरण पद्धति का पालन करते हुए:\n\n• मूल्यांकन और योजना (2 सप्ताह)\n• सिस्टम सेटअप और कॉन्फ़िगरेशन (2 सप्ताह)\n• डेटा माइग्रेशन (2 सप्ताह)\n• परीक्षण और गुणवत्ता आश्वासन (2 सप्ताह)\n• प्रशिक्षण और ज्ञान स्थानांतरण (2 सप्ताह)\n• गो-लाइव और सहायता (2 सप्ताह)\n\nआपकी विशिष्ट आवश्यकताओं और आवश्यक अनुकूलन की जटिलता के आधार पर समयसीमा भिन्न हो सकती है।`,
          videoUrl: "https://example.com/implementation-video"
        },
        {
          category: "लागत और मूल्य निर्धारण",
          question: "कार्यान्वयन सहित कुल लागत क्या है?",
          answer: `हमारा मूल्य निर्धारण बिना छुपी लागत के पारदर्शी है:\n\n• सॉफ्टवेयर लाइसेंस: ₹15,000/महीना से शुरू\n• कार्यान्वयन: ₹5,00,000 की एकमुश्त फीस (एंटरप्राइज प्लान में शामिल)\n• प्रशिक्षण: सभी प्लान में शामिल\n• सहायता: 24/7 सहायता शामिल\n• अनुकूलन: आवश्यकताओं के आधार पर अलग से कोटेशन\n\nसभी कीमतों में 18% GST शामिल है। हम लचीली भुगतान शर्तें और वित्तपोषण विकल्प भी प्रदान करते हैं।`
        },
        {
          category: "विशेषताएं",
          question: "क्या सिस्टम एल्यूमीनियम-विशिष्ट आवश्यकताओं जैसे मिश्र धातु ट्रैकिंग को संभालता है?",
          answer: `हां, हमारा ERP विशेष रूप से एल्यूमीनियम एक्सट्रूज़न निर्माताओं के लिए डिज़ाइन किया गया है:\n\n• मिश्र धातु संरचना ट्रैकिंग और प्रबंधन\n• डाई प्रबंधन और रखरखाव शेड्यूलिंग\n• एक्सट्रूज़न प्रक्रिया पैरामीटर निगरानी\n• हीट ट्रीटमेंट ट्रैकिंग\n• सतह उपचार प्रबंधन\n• एल्यूमीनियम-विशिष्ट गुणवत्ता नियंत्रण प्रोटोकॉल\n• स्क्रैप और रीसाइक्लिंग प्रबंधन\n• उद्योग-विशिष्ट रिपोर्टिंग और विश्लेषण`
        },
        {
          category: "एकीकरण",
          question: "क्या यह हमारे मौजूदा अकाउंटिंग सॉफ्टवेयर और मशीनों के साथ एकीकृत हो सकता है?",
          answer: `हां, हम व्यापक एकीकरण का समर्थन करते हैं:\n\n• अकाउंटिंग सॉफ्टवेयर: Tally, SAP, QuickBooks, Zoho Books\n• निर्माण उपकरण: PLC एकीकरण, IoT सेंसर\n• गुणवत्ता उपकरण: CMM मशीनें, परीक्षण उपकरण\n• लॉजिस्टिक्स: परिवहन प्रबंधन सिस्टम\n• बैंकिंग: पेमेंट गेटवे, बैंक समाधान\n• ई-कॉमर्स: B2B पोर्टल, मार्केटप्लेस एकीकरण\n\nहमारी API-फर्स्ट आर्किटेक्चर अधिकांश तृतीय-पक्ष सिस्टम के साथ निर्बाध कनेक्टिविटी सुनिश्चित करती है।`
        },
        {
          category: "डेटा सुरक्षा",
          question: "हमारा डेटा कितना सुरक्षित है और अनुपालन के बारे में क्या?",
          answer: `हम एंटरप्राइज-ग्रेड सुरक्षा बनाए रखते हैं:\n\n• ISO 27001 प्रमाणित डेटा केंद्र\n• डेटा ट्रांसमिशन के लिए 256-बिट SSL एन्क्रिप्शन\n• डेटा रेस्ट के लिए AES-256 एन्क्रिप्शन\n• नियमित सुरक्षा ऑडिट और पेनेट्रेशन टेस्टिंग\n• GDPR और भारतीय डेटा सुरक्षा अनुपालन\n• भूमिका-आधारित एक्सेस नियंत्रण\n• 99.9% अपटाइम SLA के साथ स्वचालित दैनिक बैकअप\n• 4-घंटे RTO के साथ आपदा रिकवरी`
        },
        {
          category: "सहायता",
          question: "कार्यान्वयन के बाद आप किस प्रकार की सहायता प्रदान करते हैं?",
          answer: `हम व्यापक निरंतर सहायता प्रदान करते हैं:\n\n• फोन, ईमेल और चैट के माध्यम से 24/7 तकनीकी सहायता\n• एंटरप्राइज क्लाइंट्स के लिए समर्पित खाता प्रबंधक\n• नियमित सिस्टम स्वास्थ्य जांच और अनुकूलन\n• मुफ्त सॉफ्टवेयर अपडेट और सुरक्षा पैच\n• ऑनलाइन प्रशिक्षण संसाधन और दस्तावेज़ीकरण\n• उपयोगकर्ता समुदाय फोरम\n• ऑन-साइट सहायता उपलब्ध (चार्जेबल)\n• वार्षिक सिस्टम समीक्षा और अनुकूलन सिफारिशें`
        },
        {
          category: "अनुकूलन",
          question: "क्या सिस्टम को हमारी विशिष्ट प्रक्रियाओं के लिए अनुकूलित किया जा सकता है?",
          answer: `बिल्कुल! हमारा ERP अत्यधिक अनुकूलन योग्य है:\n\n• कस्टम वर्कफ़्लो और अनुमोदन प्रक्रियाएं\n• उद्योग-विशिष्ट फॉर्म और रिपोर्ट\n• कस्टम डैशबोर्ड और KPI ट्रैकिंग\n• अनूठी आवश्यकताओं के लिए विशेष मॉड्यूल\n• मालिकाना सिस्टम के साथ कस्टम एकीकरण\n• आपकी कंपनी के रंगों के साथ ब्रांडेड उपयोगकर्ता इंटरफ़ेस\n• विशिष्ट उपयोग मामलों के लिए कस्टम मोबाइल ऐप्स\n\nहमारी टीम आपकी विशिष्ट आवश्यकताओं को समझने और लागू करने के लिए आपके साथ मिलकर काम करती है।`
        },
        {
          category: "प्रशिक्षण",
          question: "आप कैसे सुनिश्चित करते हैं कि हमारी टीम सिस्टम का प्रभावी रूप से उपयोग कर सके?",
          answer: `हम व्यापक प्रशिक्षण कार्यक्रम प्रदान करते हैं:\n\n• विभिन्न उपयोगकर्ता प्रकारों के लिए भूमिका-आधारित प्रशिक्षण\n• आपके वास्तविक डेटा के साथ व्यावहारिक प्रशिक्षण\n• आंतरिक चैंपियन के लिए ट्रेन-द-ट्रेनर कार्यक्रम\n• वीडियो ट्यूटोरियल और दस्तावेज़ीकरण\n• नियमित रिफ्रेशर प्रशिक्षण सत्र\n• नए कर्मचारी ऑनबोर्डिंग सहायता\n• जैसे-जैसे आप बढ़ते हैं उन्नत फीचर प्रशिक्षण\n• पावर उपयोगकर्ताओं के लिए प्रमाणन कार्यक्रम`
        }
      ],
      noResults: "आपकी खोज से मेल खाने वाले कोई FAQ नहीं मिले।",
      watchVideo: "वीडियो स्पष्टीकरण देखें"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredFAQs(translations[currentLanguage].faqs);
    } else {
      const filtered = translations[currentLanguage].faqs.filter(faq =>
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFAQs(filtered);
    }
  }, [searchTerm, currentLanguage]);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const categories = [...new Set(t.faqs.map(faq => faq.category))];

  return (
    <section className="py-20 bg-surface-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary mb-8">
            {t.subtitle}
          </p>

          {/* Search Bar */}
          <div className="relative max-w-md mx-auto">
            <Input
              type="search"
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-4 py-3 w-full"
            />
            <Icon 
              name="Search" 
              size={20} 
              color="var(--color-text-muted)" 
              className="absolute left-4 top-1/2 transform -translate-y-1/2"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((category, index) => (
            <button
              key={index}
              className="px-4 py-2 bg-white rounded-full text-sm font-medium text-text-secondary hover:text-primary hover:bg-primary-50 transition-colors duration-200 border border-border"
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow duration-200"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-surface-50 transition-colors duration-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-xs font-medium text-accent bg-accent-50 px-2 py-1 rounded-full">
                        {faq.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-primary pr-4">
                      {faq.question}
                    </h3>
                  </div>
                  <Icon
                    name={openFAQ === index ? "ChevronUp" : "ChevronDown"}
                    size={24}
                    color="var(--color-text-secondary)"
                    className="flex-shrink-0 transition-transform duration-200"
                  />
                </button>

                {openFAQ === index && (
                  <div className="px-6 pb-6 border-t border-border">
                    <div className="pt-4">
                      <div className="prose prose-sm max-w-none">
                        {faq.answer.split('\n').map((paragraph, pIndex) => (
                          <p key={pIndex} className="text-text-secondary leading-relaxed mb-3 last:mb-0">
                            {paragraph}
                          </p>
                        ))}
                      </div>

                      {/* Video Link if available */}
                      {faq.videoUrl && (
                        <div className="mt-6 pt-4 border-t border-border">
                          <button className="inline-flex items-center space-x-2 text-primary hover:text-primary-600 font-medium">
                            <Icon name="Play" size={16} />
                            <span>{t.watchVideo}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Icon name="Search" size={48} color="var(--color-text-muted)" className="mx-auto mb-4" />
              <p className="text-text-muted text-lg">{t.noResults}</p>
            </div>
          )}
        </div>

        {/* Contact Support */}
        <div className="mt-16 bg-white rounded-2xl p-8 text-center shadow-lg border border-border">
          <Icon name="Headphones" size={48} color="var(--color-primary)" className="mx-auto mb-4" />
          <h3 className="text-xl font-bold text-primary mb-4">
            {currentLanguage === 'en' ? 'Still have questions?' : 'अभी भी प्रश्न हैं?'}
          </h3>
          <p className="text-text-secondary mb-6">
            {currentLanguage === 'en' ?'Our expert team is here to help you with any specific questions about our ERP system.' :'हमारी विशेषज्ञ टीम हमारे ERP सिस्टम के बारे में आपके किसी भी विशिष्ट प्रश्न में आपकी सहायता के लिए यहां है।'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="Phone" size={16} />
              <span>+91 98765 43210</span>
            </div>
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="Mail" size={16} />
              <span>support@alumerp.com</span>
            </div>
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="MessageCircle" size={16} />
              <span>
                {currentLanguage === 'en' ? 'Live Chat' : 'लाइव चैट'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;